import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, FilePlus2, Database, LogOut, Factory, LogIn, MessageSquare, CalendarDays, FileText, Package } from "lucide-react";
import { Button } from "../components/ui/button";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", public: true },
  { to: "/input", label: "Input Laporan", icon: FilePlus2, testid: "nav-input", public: false },
  { to: "/stock-aa", label: "Stock AA", icon: Package, testid: "nav-stock-aa", public: false },
  { to: "/meeting", label: "Info Meeting", icon: MessageSquare, testid: "nav-meeting", public: false },
  { to: "/data", label: "Data Laporan", icon: Database, testid: "nav-data", public: false },
  { to: "/wtr-si", label: "WTR SI", icon: CalendarDays, testid: "nav-wtr-si", public: true },
  { to: "/schedule", label: "Schedule", icon: FileText, testid: "nav-schedule", public: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/dashboard");
  };

  const handleNavClick = (item, e) => {
    if (!item.public && !user) {
      e.preventDefault();
      navigate("/login", { state: { from: item.to } });
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden md:flex md:flex-col w-60 border-r border-slate-200 bg-white">
        <div className="h-14 flex items-center px-5 border-b border-slate-200">
          <Factory className="w-5 h-5 text-blue-700 mr-2" strokeWidth={2} />
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase text-slate-500 font-semibold">Rodding</div>
            <div className="text-sm font-bold text-slate-900 -mt-0.5">Report System</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const locked = !item.public && !user;
            return (
              <NavLink
                key={item.to}
                to={locked ? "/login" : item.to}
                data-testid={item.testid}
                onClick={(e) => handleNavClick(item, e)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                    (isActive && !locked)
                      ? "bg-blue-50 text-blue-700 border-l-2 border-blue-700 -ml-0.5 pl-[10px]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={2} />
                <span className="flex-1">{item.label}</span>
                {locked && <span className="text-[10px] text-slate-400 uppercase tracking-wider">Login</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          {user ? (
            <div className="flex items-center justify-between">
              <div className="text-xs">
                <div className="label-ind">Operator</div>
                <div className="font-semibold text-slate-900" data-testid="user-name">{user.username}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="btn-logout" className="h-8 w-8 p-0 text-slate-500 hover:text-red-600">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button onClick={() => navigate("/login", { state: { from: location.pathname } })} data-testid="btn-sidebar-login" className="w-full h-9 rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              <LogIn className="w-4 h-4 mr-1" /> Masuk
            </Button>
          )}
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 h-12 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Factory className="w-4 h-4 text-blue-700" />
          <span className="font-bold text-sm text-slate-900">Rodding Report</span>
        </div>
        {user ? (
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="btn-logout-mobile" className="h-8 text-slate-500">
            <LogOut className="w-4 h-4" />
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="btn-login-mobile" className="h-8 text-slate-700">
            <LogIn className="w-4 h-4" />
          </Button>
        )}
      </div>

      <main className="flex-1 pt-12 md:pt-0 overflow-x-hidden">
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 grid grid-cols-6">
          {navItems.map((item) => {
            const locked = !item.public && !user;
            return (
              <NavLink
                key={item.to}
                to={locked ? "/login" : item.to}
                onClick={(e) => handleNavClick(item, e)}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2 text-[11px] font-medium relative ${
                    (isActive && !locked) ? "text-blue-700" : "text-slate-500"
                  }`
                }
              >
                <item.icon className="w-4 h-4 mb-0.5" />
                {item.label}
                {locked && <span className="absolute top-1 right-2 text-[9px] text-slate-400">🔒</span>}
              </NavLink>
            );
          })}
        </div>
        <div className="pb-16 md:pb-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
